import { Accordion, AccordionHeader } from '@ps/isp-web-component-library';
import type { MyBet } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import RegulatorBetId from '@swf/sports/components/RegulatorBetId/RegulatorBetId';

import { useSportsMyBetsWidget } from '../../../../../../model';

import {
  TXTWCL_Bet_ID,
  TXTWCL_Bet_PlacedX_N0,
  TXTWCL_Cashed_out,
  TXTWCL_Date_SettledX_N0,
  TXTWCL_Open_11,
  TXTWCL_Settled_1,
  TXTWCL_StatusX_N0
} from './BetDetails.messages.json';
import styles from './BetDetails.scss';
import PromotionHistory from './components/PromotionHistory/PromotionHistory';
import { Title } from './components/Title/Title';
import betStatusToTranslationTagMap from './utils/betStatusToTranslationTagMap';

interface Props {
  bet: MyBet;
  cashedOutAmount?: number;
}

const BetDetails: React.FC<Props> = ({ bet, cashedOutAmount }) => {
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const {
    widgetConfig: { isEmbedded }
  } = useSportsMyBetsWidget();

  const {
    result,
    isSettled,
    betReceiptId,
    placedDate,
    settledDate,
    walletInfo: { hasBonusCash, hasFreeBets },
    regulatorId
  } = bet;
  let betStatus: TranslationTag = TXTWCL_Open_11;
  const displayPromotionHistory =
    (hasBonusCash || hasFreeBets) && !isSettled && !isSelfExcluded && !isEmbedded;

  if (result) {
    betStatus = betStatusToTranslationTagMap[result];
  } else if (!isSettled && !!cashedOutAmount) {
    betStatus = TXTWCL_Cashed_out;
  } else if (isSettled) {
    betStatus = TXTWCL_Settled_1;
  }

  return (
    <div className={styles.wrapper}>
      <Accordion
        variant="secondary"
        isInitiallyExpanded={false}
        renderTitle={(props) => <AccordionHeader {...props} title={<Title />} />}
      >
        <div className={styles.content}>
          <ul className={styles.list}>
            {regulatorId && (
              <li>
                <RegulatorBetId regulatorId={regulatorId} />
              </li>
            )}

            <li>
              <Translation {...TXTWCL_Bet_ID} />: <span>{betReceiptId}</span>
            </li>

            <li>
              <Translation
                {...TXTWCL_Bet_PlacedX_N0}
                values={{
                  0: (
                    <span>
                      <FormattedDateTime date={placedDate} format="short" />
                    </span>
                  )
                }}
              />
            </li>

            <li>
              <Translation
                {...TXTWCL_StatusX_N0}
                values={{
                  0: (
                    <span>
                      <Translation {...betStatus} />
                      {displayPromotionHistory && (
                        <PromotionHistory bet={bet} isBonusCash={hasBonusCash} />
                      )}
                    </span>
                  )
                }}
              />
            </li>
            {isSettled && settledDate && (
              <li>
                <Translation
                  {...TXTWCL_Date_SettledX_N0}
                  values={{
                    0: (
                      <span>
                        <FormattedDateTime date={settledDate} format="short" />
                      </span>
                    )
                  }}
                />
              </li>
            )}
          </ul>
        </div>
      </Accordion>
    </div>
  );
};

export default BetDetails;
